import { Link, createFileRoute } from '@tanstack/react-router'
import { Clapperboard, CreditCard, Loader2 } from 'lucide-react'
import { IntegrationCard } from './-components/IntegrationCard'
import { PageTitle } from '@/components/PageTitle'
import { BrandLogo } from '@/components/shared/BrandLogo'
import { RothRadarTargetIcon } from '@/components/shared/RothRadarTargetIcon'
import { GoogleDriveIcon, HubSpotIcon } from '@/components/ExternalLinks'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { requireAnyPermission } from '@/lib/permissions/route-guards'
import {
  BACKOFFICE_INTEGRATIONS_PREFIX,
  BACKOFFICE_PERMISSION_KEYS,
  ROTH_RADAR_PREFIX,
} from '@/lib/permissions/backoffice-permissions'
import { useStalledAlertsStatus } from '@/hooks/repositories/useSlackStalledAlertsRepository'
import { useMeetingTranscriptsStatus } from '@/hooks/repositories/useMeetingTranscriptsRepository'
import { meetingTranscriptsCardStatus } from '@/lib/meeting-transcripts'
import { useCan } from '@/hooks/permissions/use-can'

export const Route = createFileRoute('/_app/backoffice/integrations/')({
  component: RouteComponent,
  beforeLoad: requireAnyPermission(BACKOFFICE_INTEGRATIONS_PREFIX),
})

function RouteComponent() {
  const { has, hasAnyWithPrefix, permissionsResolved } = useCan()

  // Google Drive's card covers either key it hosts — Recommended Files or
  // the ShareFile Migration tab (sc-1215).
  const showGoogleDrive =
    has(BACKOFFICE_PERMISSION_KEYS.googleDrive) ||
    has(BACKOFFICE_PERMISSION_KEYS.sharefileMigration)

  // Only ask the API when the caller can see the card; otherwise it would 403.
  const slackStatus = useStalledAlertsStatus({
    enabled: has(BACKOFFICE_PERMISSION_KEYS.slack),
  })
  const slackCardStatus = slackStatus.data
    ? slackStatus.data.slackConfigured
      ? 'connected'
      : 'disconnected'
    : 'unknown'

  // Same for meeting transcripts: only ask when the card is visible.
  // "Connected" means at least one source can actually bring meetings in.
  const meetingTranscriptsStatus = useMeetingTranscriptsStatus({
    enabled: has(BACKOFFICE_PERMISSION_KEYS.meetingTranscripts),
  })
  const meetingTranscriptsCardState = meetingTranscriptsCardStatus(
    meetingTranscriptsStatus.data,
  )

  const cards = [
    has(BACKOFFICE_PERMISSION_KEYS.hubspot) && (
      <IntegrationCard
        key="hubspot"
        title="HubSpot"
        description="CRM integration for contact and deal management. Sync users and clients, view webhook events and subscription health."
        icon={<HubSpotIcon className="size-5 shrink-0" />}
        status="connected"
        to="/backoffice/integrations/hubspot"
      />
    ),
    has(BACKOFFICE_PERMISSION_KEYS.stripe) && (
      <IntegrationCard
        key="stripe"
        title="Stripe"
        description="Payments integration for signed webhook visibility, customers, payments, invoices, subscriptions, and reconciliation."
        icon={<CreditCard className="size-5 shrink-0 text-[#635bff]" />}
        status="connected"
        to="/backoffice/integrations/stripe"
      />
    ),
    has(BACKOFFICE_PERMISSION_KEYS.mux) && (
      <IntegrationCard
        key="mux"
        title="Mux"
        description="Video hosting for Learning. Usage against the free allowances, and where charges start accruing — storage bills from the first minute, delivery and Robots are free to 100,000 a month."
        icon={<Clapperboard className="size-5 shrink-0 text-[#fa50b5]" />}
        status="connected"
        to="/backoffice/integrations/mux"
      />
    ),
    showGoogleDrive && (
      <IntegrationCard
        key="google-drive"
        title="Google Drive"
        description="Drive folder mirror for customer-facing content. Manage the recommended readings shown across client portals."
        icon={<GoogleDriveIcon className="size-5 shrink-0" />}
        status="connected"
        to="/backoffice/integrations/google-drive"
      />
    ),
    hasAnyWithPrefix(ROTH_RADAR_PREFIX) && (
      <IntegrationCard
        key="roth-radar"
        title="RothRadar™"
        description="Rules engine admin console for ticker updates, position lookups, schedules, and rule definitions, plus the firm-wide proxy map for holdings RothRadar™ can't price directly, plus daily CPA approval of the automated client alert send."
        icon={
          <RothRadarTargetIcon className="size-5 shrink-0 text-red-600 dark:text-red-400" />
        }
        status="connected"
        to="/backoffice/integrations/roth-radar"
      />
    ),
    has(BACKOFFICE_PERMISSION_KEYS.plaid) && (
      <IntegrationCard
        key="plaid"
        title="Plaid"
        description="Account aggregation health and spend. Track linked Items, accounts, connection errors, and the estimated monthly run-rate."
        icon={<BrandLogo domain="plaid.com" name="Plaid" className="size-5" />}
        status="connected"
        to="/backoffice/integrations/plaid"
      />
    ),
    has(BACKOFFICE_PERMISSION_KEYS.front) && (
      <IntegrationCard
        key="front"
        title="Front"
        description="Help-desk article source for the client portal. Check and clear the Redis cache for help-center content served to clients."
        icon={<BrandLogo domain="front.com" name="Front" className="size-5" />}
        status="connected"
        to="/backoffice/integrations/front"
      />
    ),
    has(BACKOFFICE_PERMISSION_KEYS.slack) && (
      <IntegrationCard
        key="slack"
        title="Slack"
        description="Stalled Service and Stalled Deals alerts. Register the Slack channels, tune the thresholds, and review open items."
        icon={<BrandLogo domain="slack.com" name="Slack" className="size-5" />}
        status={slackCardStatus}
        to="/backoffice/integrations/slack"
      />
    ),
    has(BACKOFFICE_PERMISSION_KEYS.meetingTranscripts) && (
      <IntegrationCard
        key="meeting-transcripts"
        title="Meeting transcripts"
        description="Calls recorded by Zoom and Avoma, each call once with its transcripts, Sales or Service: per-source status, backfill, and Zoom webhook deliveries."
        icon={
          <span className="flex shrink-0 -space-x-1.5">
            <BrandLogo
              domain="zoom.us"
              name="Zoom"
              className="size-5 ring-2 ring-card"
            />
            <BrandLogo
              domain="avoma.com"
              name="Avoma"
              className="size-5 ring-2 ring-card"
            />
          </span>
        }
        status={meetingTranscriptsCardState}
        to="/backoffice/integrations/meeting-transcripts"
      />
    ),
  ].filter(Boolean)

  return (
    <div className="py-4">
      <PageTitle title="Integrations" />

      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/backoffice">Backoffice</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Integrations</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Integrations</h1>
        <p className="text-muted-foreground mt-2">
          Manage external service connections and data synchronization.
        </p>
      </div>

      {!permissionsResolved ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : cards.length === 0 ? (
        <p className="py-16 text-center text-sm text-muted-foreground">
          You don't have access to any integrations.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cards}
        </div>
      )}
    </div>
  )
}
